import React, { useState, useEffect, useRef } from 'react';

const CapabilityImprovementProcess = () => {
  const steps = [
    { name: 'Define', message: 'The opportunity the business is trying to capture' },
    { name: 'Prioritize', message: 'Key sales capabilities to develop / advance' },
    { name: 'Uncover', message: 'The gaps across the sales function' },
    { name: 'Plan', message: 'A North Star Journey map' },
    { name: 'Execute', message: 'The journey map with a focus on OCM and KPIs' }
  ];

  const [activeStepIndex, setActiveStepIndex] = useState(2); // Start with "Uncover"
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const intervalRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-rotation function
  const startAutoRotation = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      setActiveStepIndex((prevIndex) => (prevIndex + 1) % steps.length);
    }, 3000); // Change every 3 seconds
  };

  // Start auto-rotation on component mount
  useEffect(() => {
    startAutoRotation();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Handle circle click
  const handleCircleClick = (index) => {
    setActiveStepIndex(index);
    // Restart auto-rotation from the clicked step
    startAutoRotation();
  };

  // Responsive dimensions
  const getResponsiveDimensions = () => {
    if (windowWidth <= 480) {
      return {
        containerSize: 280,
        stepContainerSize: 240,
        circleRadius: 100,
        stepCircleSize: 60,
        stepFontSize: '10px',
        centerTextWidth: 140,
        centerFontSize: '12px',
        svgSize: 240
      };
    } else if (windowWidth <= 768) {
      return {
        containerSize: 350,
        stepContainerSize: 300,
        circleRadius: 125,
        stepCircleSize: 70,
        stepFontSize: '12px',
        centerTextWidth: 160,
        centerFontSize: '14px',
        svgSize: 300
      };
    } else if (windowWidth <= 1024) {
      return {
        containerSize: 450,
        stepContainerSize: 400,
        circleRadius: 170,
        stepCircleSize: 90,
        stepFontSize: '14px',
        centerTextWidth: 200,
        centerFontSize: '15px',
        svgSize: 400
      };
    } else {
      return {
        containerSize: 600,
        stepContainerSize: 500,
        circleRadius: 210,
        stepCircleSize: 120,
        stepFontSize: '16px',
        centerTextWidth: 260,
        centerFontSize: '20px',
        svgSize: 500
      };
    }
  };

  const dimensions = getResponsiveDimensions();

  const styles = {
    improvementContainer: {
      // minHeight: '100vh',
      minHeight: windowWidth <= 768 ? '60vh' : '100vh',
      backgroundColor: '#2C2C2C',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: 'Arial, sans-serif',
      padding: windowWidth <= 480 ? '10px' : '20px',
    },
    
    content: {
      textAlign: 'center',
      zIndex: 2,
      position: 'relative',
      width: '100%',
      maxWidth: '1200px',
    },
    
    title: {
      color: 'white',
      fontSize: windowWidth <= 480 ? '1.5rem' : windowWidth <= 768 ? '2rem' : 'clamp(2rem, 5vw, 3rem)',
      marginBottom: windowWidth <= 768 ? '30px' : '60px',
      marginTop: windowWidth <= 768 ? '20px' : '60px',
      fontWeight: 'bold',
      textAlign: 'center',
      lineHeight: '1.2',
      padding: '0 10px',
    },
    
    circleContainer: {
      position: 'relative',
      width: `${dimensions.containerSize}px`,
      height: `${dimensions.containerSize}px`,
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    
    stepContainer: {
      position: 'relative',
      width: `${dimensions.stepContainerSize}px`,
      height: `${dimensions.stepContainerSize}px`,
    },
    
    stepCircle: {
      position: 'absolute',
      width: `${dimensions.stepCircleSize}px`,
      height: `${dimensions.stepCircleSize}px`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: windowWidth <= 480 ? '2px solid white' : '4px solid white',
      left: '50%',
      top: '50%',
      marginLeft: `-${dimensions.stepCircleSize / 2}px`,
      marginTop: `-${dimensions.stepCircleSize / 2}px`,
      zIndex: 2,
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    
    stepText: {
      color: 'white',
      fontSize: dimensions.stepFontSize,
      fontWeight: 'bold',
      textAlign: 'center',
      padding: '5px',
      lineHeight: '1.1',
      userSelect: 'none',
    },
    
    connectionLines: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1,
    },
    
    centerMessage: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 3,
      width: `${dimensions.centerTextWidth}px`,
    },
    
    centerText: {
      color: 'white',
      fontSize: dimensions.centerFontSize,
      lineHeight: '1.4',
      textAlign: 'center',
      fontWeight: 'bold',
      padding: '10px',
    },

  };

  return (
    <div style={styles.improvementContainer}>
      <div style={styles.content}>
        <h1 style={styles.title}>Our Capability Improvement Process</h1>
        
        <div style={styles.circleContainer}>
          <div style={styles.stepContainer}>
            {/* Connection Lines - Outer Circle */}
            <svg 
              style={styles.connectionLines} 
              width={dimensions.svgSize} 
              height={dimensions.svgSize} 
              viewBox={`-${dimensions.svgSize/2} -${dimensions.svgSize/2} ${dimensions.svgSize} ${dimensions.svgSize}`}
            >
              <circle
                cx="0"
                cy="0"
                r={dimensions.circleRadius}
                fill="none"
                stroke="white"
                strokeWidth={windowWidth <= 480 ? "2" : "4"}
                opacity="1"
              />
            </svg>
            
            {steps.map((step, index) => {
              const angle = (index * 72) - 90; // 360/5 = 72 degrees between each step, -90 to start from top
              const x = dimensions.circleRadius * Math.cos(angle * Math.PI / 180);
              const y = dimensions.circleRadius * Math.sin(angle * Math.PI / 180);
              const isActive = index === activeStepIndex;
              
              return (
                <div
                  key={step.name}
                  style={{
                    ...styles.stepCircle,
                    backgroundColor: isActive ? '#D4A853' : '#4A90E2',
                    transform: `translate(${x}px, ${y}px)`,
                  }}
                  onClick={() => handleCircleClick(index)}
                >
                  <span style={styles.stepText}>{step.name}</span>
                </div>
              );
            })}
          </div>
          
          {/* Center Message */}
          <div style={styles.centerMessage}>
            <div style={styles.centerText}>
              {steps[activeStepIndex].message}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapabilityImprovementProcess;